import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useDocumentTemplatesQuery } from "../hooks/useDocumentTemplatesQuery";
import { dataProviders } from "../services/data";
import type { DocumentTemplate } from "../types";
import { useAuth } from "./AuthContext";

interface DocumentTemplateContextType {
	templates: DocumentTemplate[];
	loading: boolean;
	error: string | null;
	addTemplate: (
		template: Omit<DocumentTemplate, "id" | "createdAt" | "updatedAt">,
	) => Promise<void>;
	updateTemplate: (
		id: string,
		updates: Partial<DocumentTemplate>,
	) => Promise<void>;
	deleteTemplate: (id: string) => Promise<void>;
	getTemplateByChecklistId: (
		checklistItemId: string,
	) => DocumentTemplate | undefined;
	refreshTemplates: () => Promise<void>;
}

const DocumentTemplateContext = createContext<
	DocumentTemplateContextType | undefined
>(undefined);

export const DocumentTemplateProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const queryClient = useQueryClient();
	const { isAuthenticated, loading: authLoading } = useAuth();

	const {
		data: templates = [],
		isLoading: loading,
		error: queryError,
		refetch,
	} = useDocumentTemplatesQuery(!!isAuthenticated && !authLoading);

	const error = queryError
		? queryError instanceof Error
			? queryError.message
			: "Failed to load templates"
		: null;

	const invalidateTemplates = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["documentTemplates"] });
	}, [queryClient]);

	const addTemplateMutation = useMutation({
		mutationFn: (
			template: Omit<DocumentTemplate, "id" | "createdAt" | "updatedAt">,
		) => dataProviders.documentTemplate.create(template),
		onSuccess: () => invalidateTemplates(),
	});

	const updateTemplateMutation = useMutation({
		mutationFn: ({
			id,
			updates,
		}: {
			id: string;
			updates: Partial<DocumentTemplate>;
		}) => dataProviders.documentTemplate.update(id, updates),
		onSuccess: () => invalidateTemplates(),
	});

	const deleteTemplateMutation = useMutation({
		mutationFn: (id: string) => dataProviders.documentTemplate.delete(id),
		onSuccess: () => invalidateTemplates(),
	});

	const getTemplateByChecklistId = useCallback(
		(checklistItemId: string) =>
			templates.find((t) => t.checklistItemId === checklistItemId),
		[templates],
	);

	const addTemplate = useCallback(
		async (
			template: Omit<DocumentTemplate, "id" | "createdAt" | "updatedAt">,
		) => {
			await addTemplateMutation.mutateAsync(template);
		},
		[addTemplateMutation],
	);

	const updateTemplate = useCallback(
		async (id: string, updates: Partial<DocumentTemplate>) => {
			await updateTemplateMutation.mutateAsync({ id, updates });
		},
		[updateTemplateMutation],
	);

	const deleteTemplate = useCallback(
		async (id: string) => {
			await deleteTemplateMutation.mutateAsync(id);
		},
		[deleteTemplateMutation],
	);

	const refreshTemplates = useCallback(async () => {
		await refetch();
	}, [refetch]);

	const contextValue = useMemo(
		() => ({
			templates,
			loading,
			error,
			addTemplate,
			updateTemplate,
			deleteTemplate,
			getTemplateByChecklistId,
			refreshTemplates,
		}),
		[
			templates,
			loading,
			error,
			addTemplate,
			updateTemplate,
			deleteTemplate,
			getTemplateByChecklistId,
			refreshTemplates,
		],
	);

	return (
		<DocumentTemplateContext.Provider value={contextValue}>
			{children}
		</DocumentTemplateContext.Provider>
	);
};

export const useDocumentTemplates = () => {
	const context = useContext(DocumentTemplateContext);
	if (!context) {
		throw new Error(
			"useDocumentTemplates must be used within DocumentTemplateProvider",
		);
	}
	return context;
};
